import React, { useEffect, useState } from 'react';
import { useParams, useLocation } from 'react-router-dom';
import api from '../../api';
import DynamicBanner from '../Pages/include/DynamicBanner';
import '../QuickLinks/GeneralHospital/GeneralHospital.css';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Autoplay, Pagination } from "swiper/modules";
import 'swiper/css';
import 'swiper/css/effect-coverflow';
import StickyNav from '../Pages/Academics/CommitteeCouncil/Partials/StickyNav';
import NotFound from '../Pages/NotFound/NotFound';
import ZebrafishForm from '../Form/ZebrafishForm';

const DynamicPages = () => {
  const { slug } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [activeMainTab, setActiveMainTab] = useState('');
  const [activeInnerTab, setActiveInnerTab] = useState('');
  const location = useLocation();
  const isCommitteePage = location.pathname.includes('various-committees');

  // ==========================================
  // FIX: Suppress ResizeObserver loop error
  // ==========================================
  useEffect(() => {
    const errorHandler = (e) => {
      if (
        e.message === 'ResizeObserver loop completed with undelivered notifications.' ||
        e.message === 'ResizeObserver loop limit exceeded'
      ) {
        const resizeObserverErrDiv = document.getElementById(
          'webpack-dev-server-client-overlay-div'
        );
        const resizeObserverErr = document.getElementById(
          'webpack-dev-server-client-overlay'
        );
        if (resizeObserverErr) resizeObserverErr.style.display = 'none';
        if (resizeObserverErrDiv) resizeObserverErrDiv.style.display = 'none';
        e.stopImmediatePropagation();
      }
    };
    window.addEventListener('error', errorHandler);
    return () => window.removeEventListener('error', errorHandler);
  }, []);

  useEffect(() => {
    const fetchDetail = async () => {
      setLoading(true);
      try {
        const response = await api.get(`/api/dynamicpages/${slug}`);
        const fetchedData = response.data;
        setData(fetchedData);
        setNotFound(false);
        if (fetchedData.main_tabs && fetchedData.main_tabs.length > 0) {
          const firstMainTab = fetchedData.main_tabs[0].id;
          setActiveMainTab(firstMainTab);

          const firstInnerTab = fetchedData.main_tabs[0].inner_tabs?.[0]?.id || firstMainTab;
          setActiveInnerTab(firstInnerTab);
        }
      } catch (err) {
        console.error('Error fetching department details:', err);
        setError('Department not found or data error.');
        setNotFound(true);
      } finally {
        setLoading(false);
      }
    };
    fetchDetail();
  }, [slug]);

  if (notFound) return <NotFound />;
  if (loading) return null;
  if (error || !data) return null;

  const { banner_data, main_tabs, tab_content } = data;

  const getMainTabById = (id) => main_tabs.find((t) => t.id === id);
  const activeMainTabObj = getMainTabById(activeMainTab);

  const isZebrafishFacility = activeMainTabObj?.title === 'Zebrafish Facility';

  const renderDynamicBlock = (block) => {
    const {
      type,
      heading,
      text_content,
      image_url,
      image_position,
      list_items,
      table_headers,
      table_rows,
      fa_icon,
      accordion_items
    } = block;

    if (type === 'heading_block') {
      return (
        <div className="sec-title text-center py-3">
          <h2>{heading}</h2>
        </div>
      );
    }

    if (type === 'paragraph_block') {
      return (
        <p
          style={{ textAlign: 'justify' }}
          dangerouslySetInnerHTML={{ __html: text_content }}
        />
      );
    }

    if (type === 'image_only') {
      return (
        <div className="row justify-content-center mb-4">
          <div className="col-md-6 col-12 text-center">
            <img
              src={`${process.env.REACT_APP_API_URL}/public${image_url}`}
              alt={heading || 'Department Image'}
              className="img-fluid rounded"
            />
          </div>
        </div>
      );
    }

    if (type === 'anchor_tag') {
      return (
        <p>
          <strong>Click Here for :</strong>
          <a
            href={image_url}
            target="_blank"
            rel="noopener noreferrer"
            className="d-inline-flex align-items-center gap-2 ms-2"
          >
            <i className="fa fa-hand-o-right text-danger" />
            {text_content}
          </a>
        </p>
      );
    }

    if (type === 'document_upload_link') {
      if (!image_url) return null;

      const fileUrl = `${process.env.REACT_APP_API_URL}/public${image_url}`;
      const label = heading || 'Download document';

      return (
        <p className="mb-2">
          <div className="pdf-link-card">
            <a
              href={fileUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="d-inline-flex align-items-center gap-2 "
            > <div className="pdf-link-icon">
                <i className="fa fa-file-pdf-o " aria-hidden="true" /></div>
              <div className="pdf-link-text"> {label}   </div>
            </a>
          </div>
        </p>
      );
    }

    if (type === 'video_link_upload') {
      const count = Number(block.count) || 1;
      const primaryFileSrc = image_url ? `${process.env.REACT_APP_API_URL}/public${image_url}` : null;
      const primaryEmbed = text_content ? text_content : null;
      const primaryTitle = heading || (count === 2 ? 'Video 1' : 'Video');
      const secondFileSrc = block.second_image_url ? `${process.env.REACT_APP_API_URL}/public${block.second_image_url}` : null;
      const secondEmbed = block.second_text_content ? block.second_text_content : null;
      const secondTitle = block.second_heading || 'Video 2';

      const renderSingleVideo = (fileSrc, embedSrc, title, idx) => {
        if (!fileSrc && !embedSrc) return null;

        if (typeof embedSrc === 'string' && embedSrc.trim().startsWith('<iframe')) {
          return (
            <div className="mb-4">
              {title && <h4 className="mb-2">{title}</h4>}
              <div className="iframe-wrapper" dangerouslySetInnerHTML={{ __html: embedSrc }} />
            </div>
          );
        }

        if (embedSrc) {
          return (
            <div className="mb-4">
              {title && <h4 className="mb-2">{title}</h4>}
              <div className="iframe-wrapper">
                <iframe
                  src={embedSrc}
                  title={`video-${idx}`}
                  allowFullScreen
                  style={{ width: '100%', height: 360, border: 0 }}
                />
              </div>
            </div>
          );
        }

        if (fileSrc) {
          return (
            <div className="mb-4">
              {title && <h4 className="mb-2">{title}</h4>}
              <div className="dynamic-video-wrapper">
                <video src={fileSrc} controls style={{ width: '100%', maxHeight: 480 }}>
                  Your browser does not support the video tag.
                </video>
              </div>
            </div>
          );
        }

        return null;
      };

      if (count === 2) {
        return (
          <div className="mb-4">
            <div className="row">
              <div className="col-md-6 col-12 mb-3">
                {renderSingleVideo(primaryFileSrc, primaryEmbed, primaryTitle, 1) || (
                  <div className="text-muted">No primary video provided.</div>
                )}
              </div>

              <div className="col-md-6 col-12 mb-3">
                {renderSingleVideo(secondFileSrc, secondEmbed, secondTitle, 2) || (
                  <div className="text-muted">No second video provided.</div>
                )}
              </div>
            </div>
          </div>
        );
      }

      return (
        <div className="mb-4">
          {renderSingleVideo(primaryFileSrc, primaryEmbed, primaryTitle, 1)}
        </div>
      );
    }

    if (type === 'section_image' || type === 'normal_section') {
      const hasImage = type === 'section_image' && image_url;
      const imgCol = hasImage ? 4 : 0;
      const textCol = 12 - imgCol;
      const isLeft = image_position === 'left';

      return (
        <div className="row d-flex align-items-center ">
          <div
            className={`col-md-${textCol} col-12 ${hasImage && isLeft ? 'order-2' : 'order-1'
              }`}
          >
            {heading && <h4>{heading}</h4>}
            <p dangerouslySetInnerHTML={{ __html: text_content }} />
          </div>
          {hasImage && (
            <div
              className={`col-md-${imgCol} col-12 text-center ${hasImage && isLeft ? 'order-1' : 'order-2'
                }`}
            >
              <img
                src={`${process.env.REACT_APP_API_URL}/public${image_url}`}
                alt={heading}
                className="img-fluid rounded"
                style={{ maxWidth: '100%' }}
              />
            </div>
          )}
        </div>
      );
    }

    if (type === 'list_items') {
      const itemsArray = Array.isArray(list_items)
        ? list_items
        : Object.values(list_items || {});
      const iconClass = fa_icon || 'fa-dot-circle-o';

      return (
        <div>
          {heading && (
            <div className="sec-title text-center py-3">
              <h2>{heading}</h2>
            </div>
          )}
          <ul className="list-unstyled fa-ul dep-list-content opcare-list visitor m-0">
            {itemsArray.map((item, i) => (
              <li key={i}>
                <i className={`fa ${iconClass} me-2`} /> {item}
              </li>
            ))}
          </ul>
        </div>
      );
    }

    if (type === 'table_block') {
      const headers = Array.isArray(table_headers)
        ? table_headers
        : Object.values(table_headers || {});

      let rows = [];

      if (Array.isArray(table_rows)) {
        rows = table_rows.map(r =>
          Array.isArray(r) ? r : Object.values(r || {})
        );
      } else if (typeof table_rows === "object" && table_rows !== null) {
        rows = Object.values(table_rows).map(r =>
          Array.isArray(r) ? r : Object.values(r || {})
        );
      } else {
        rows = [];
      }

      return (
        <div className="table-responsive mb-4">
          {heading && <h4 className="my-3">{heading}</h4>}
          <table className="table table-bordered table-striped dep-table">
            {headers.length > 0 && (
              <thead>
                <tr>
                  {headers.map((header, hIdx) => (
                    <th key={hIdx}>{header}</th>
                  ))}
                </tr>
              </thead>
            )}

            <tbody>
              {rows.length > 0 ? (
                rows.map((row, rIdx) => (
                  <tr key={rIdx}>
                    {row.map((cell, cIdx) => (
                      <td key={cIdx} dangerouslySetInnerHTML={{ __html: cell }} />
                    ))}
                  </tr>
                ))
              ) : null}
            </tbody>
          </table>
        </div>
      );
    }

    if (type === 'carousel_images') {
      const images = block.carousel_images_data || [];
      const perView = Number(block.carousel_count) || 1;

      if (!images.length) return null;

      return (
        <div className="hospital-slider mb-4">
          <Swiper
            grabCursor={false}
            centeredSlides={false}
            loop={false}
            spaceBetween={20}
            autoplay={{
              delay: 2500,
              disableOnInteraction: true,
            }}
              pagination={{
    clickable: true,
    enabled: true,   
  }}

  breakpoints={{
    0: {
      slidesPerView: 1,
      pagination: {
        enabled: true,   
      },
    },
    768: {
      slidesPerView: Math.min(perView, images.length),
      pagination: {
        enabled: false,  
      },
    },
  }}
            modules={[Autoplay]}
            className="commonSwiper"
          >
            {images.map((img, index) => {
              // Check if URL is a video
              const isVideo = img.url.toLowerCase().endsWith('.mp4') || 
                              img.url.toLowerCase().endsWith('.webm') || 
                              img.url.toLowerCase().endsWith('.ogg');

              return (
                <SwiperSlide key={index}>
                  {isVideo ? (
                    <video 
                      src={`${process.env.REACT_APP_API_URL}/public${img.url}`} 
                      className="swiper-slide-img"
                      controls
                      style={{ width: '100%', height: 'auto', borderRadius: '10px' }}
                    />
                  ) : (
                    <img
                      src={`${process.env.REACT_APP_API_URL}/public${img.url}`}
                      alt={img.alt || `Department image ${index + 1}`}
                      className="swiper-slide-img"
                    />
                  )}
                  {img.text && (
                    <div className="carousel-caption">
                      {img.text}
                    </div>
                  )}
                </SwiperSlide>
              );
            })}
          </Swiper>
        </div>
      );
    }

    if (type === 'accordion') {
      const items = Array.isArray(accordion_items)
        ? accordion_items
        : Object.values(accordion_items || {});

      if (!items.length) return null;

      const accId = `accordion-${block.id || Math.random()}`;

      return (
        <div className="mb-4">
          {heading && (
            <div className="sec-title text-center py-3">
              <h2>{heading}</h2>
            </div>
          )}

          <div className="accordion" id={accId}>
            {items.map((item, index) => {
              const collapseId = `${accId}-item-${index}`;

              return (
                <div className="accordion-item" key={collapseId}>
                  <h2 className="accordion-header">
                    <button
                      className={`accordion-button ${index !== 0 ? 'collapsed' : ''}`}
                      type="button"
                      data-bs-toggle="collapse"
                      data-bs-target={`#${collapseId}`}
                      aria-expanded={index === 0}
                      aria-controls={collapseId}
                    >
                      {item.title || `Item ${index + 1}`}
                    </button>
                  </h2>

                  <div
                    id={collapseId}
                    className={`accordion-collapse collapse ${index === 0 ? 'show' : ''}`}
                    data-bs-parent={`#${accId}`}
                  >
                    <div
                      className="accordion-body"
                      dangerouslySetInnerHTML={{ __html: item.content }}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      );
    }


    return <p className="text-warning">Unknown content block type: {type}</p>;
  };

  const renderBlocksForTab = (tabId) => {
    const blocksToRender = tab_content[tabId] || [];
    if (!blocksToRender.length) {
      return <div className="alert alert-info">No content defined for this section.</div>;
    }
    let skipFirstHeading = true;

    return blocksToRender.map((block, index) => {
      if (block.type === 'heading_block' && skipFirstHeading) {
        skipFirstHeading = false;
        return null;
      }
      return <div key={block.id || index}>{renderDynamicBlock(block)}</div>;
    });
  };

  const renderMainDynamicContent = () => {
    const mainBlocks = tab_content['main_dynamic_page_content'] || [];
    if (!mainBlocks.length) return null;

    return (
      <div className="document-box document-box-medicine-medicine mb-4">
        {mainBlocks.map((block, idx) => (
          <div key={block.id || idx}>{renderDynamicBlock(block)}</div>
        ))}
      </div>
    );
  };

  return (
    <>
      <DynamicBanner
        title={banner_data.title}
        bgImage={banner_data.desktop_images}
        bgImageMobile={banner_data.mobile_images}
      />
      {isCommitteePage && <StickyNav />}
      <section className="pt_80 pb_80 general-department-section general-hospital-section dynamic-page-wrapper">
        <div className="container">
          {renderMainDynamicContent()}

          {main_tabs.length > 0 && (
            <div className="row">
              <div className="col-md-3 d-none d-md-block">
                <div className="sidebar sidebar-medicine-medicine-medicine">
                  <nav className="nav flex-column nav-pills" id="contentTabs" role="tablist">
                    {main_tabs.map((mainTab) => (
                      <button
                        key={mainTab.id}
                        className={`nav-link ${mainTab.id === activeMainTab ? 'active' : ''
                          } ${mainTab.inner_tabs?.length ? 'has-inner-tabs' : ''}`}
                        onClick={() => {
                          setActiveMainTab(mainTab.id);
                          setActiveInnerTab(
                            mainTab.inner_tabs?.[0]?.id || mainTab.id
                          );
                        }}
                        type="button"
                        role="tab"
                      >
                        {mainTab.title}
                      </button>
                    ))}
                  </nav>
                </div>
              </div>

              <div className="col-md-9 col-12 d-none d-md-block">
                <div className="specialty-block">
                  <div className="tab-content dep-content hospital-content">
                    <div
                      className="tab-pane fade show active"
                      id={`${activeMainTab}-desktop-wrapper`}
                    >
                      <div className="document-box document-box-medicine-medicine">
                        {activeMainTabObj && (
                          <div className="sec-title text-center py-3">
                            <h2>
                              {tab_content[activeMainTab]?.find(
                                (b) => b.type === 'heading_block'
                              )?.heading || activeMainTabObj.title}
                            </h2>
                          </div>
                        )}

                        {activeMainTabObj?.inner_tabs?.length ? (
                          <>
                            <ul
                              className="nav nav-tabs general-tabs d-flex justify-content-center align-items-center mb-3"
                              id="campusTabs"
                              role="tablist"
                            >
                              {activeMainTabObj.inner_tabs.map((innerTab) => (
                                <li
                                  className="nav-item"
                                  role="presentation"
                                  key={innerTab.id}
                                >
                                  <button
                                    className={`nav-link ${innerTab.id === activeInnerTab
                                        ? 'active'
                                        : ''
                                      }`}
                                    type="button"
                                    role="tab"
                                    onClick={() => setActiveInnerTab(innerTab.id)}
                                  >
                                    {innerTab.title}
                                  </button>
                                </li>
                              ))}
                            </ul>

                            {renderBlocksForTab(activeInnerTab)}
                          </>
                        ) : (
                          renderBlocksForTab(activeMainTab)
                        )}

                        {isZebrafishFacility && (
                          <div className="mb-4">
                            <ZebrafishForm />
                          </div>
                        )}

                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="col-12 d-md-none">
                <div className="accordion" id="DynamicPagemobileAccordion">
                  {main_tabs.map((mainTab) => {
                    const isActiveMainTab = mainTab.id === activeMainTab;
                    const hasInnerTabs = mainTab.inner_tabs?.length > 0;
                    const activeInnerTabForMobile = hasInnerTabs
                      ? (mainTab.inner_tabs.find(it => it.id === activeInnerTab) || mainTab.inner_tabs[0])
                      : null;

                    return (
                      <div className="accordion-item" key={mainTab.id}>
                        <h2 className="accordion-header">
                          <button
                            className={`accordion-button ${isActiveMainTab ? '' : 'collapsed'}`}
                            type="button"
                            onClick={() => {
                              if (isActiveMainTab) {
                                setActiveMainTab(null);
                              } else {
                                setActiveMainTab(mainTab.id);
                                if (hasInnerTabs) {
                                  setActiveInnerTab(mainTab.inner_tabs[0].id);
                                }
                              }
                            }}
                            aria-expanded={isActiveMainTab}
                            aria-controls={`collapse-${mainTab.id}`}
                          >
                            {mainTab.title}
                            {/* {hasInnerTabs && (
                              <span className="badge bg-secondary ms-2">
                                {mainTab.inner_tabs.length}
                              </span>
                            )} */}
                          </button>
                        </h2>

                        <div
                          id={`collapse-${mainTab.id}`}
                          className={`accordion-collapse collapse ${isActiveMainTab ? 'show' : ''}`}
                          data-bs-parent="#mobileAccordion"
                        >
                          <div className="accordion-body">
                            <div className="sec-title text-center py-3">
                              <h3 className="h5">
                                {tab_content[mainTab.id]?.find(
                                  (b) => b.type === 'heading_block'
                                )?.heading || mainTab.title}
                              </h3>
                            </div>

                            {hasInnerTabs && (
                              <div className="mb-4">
                                <div className="nav-scrollable">
                                  <div className="d-flex flex-nowrap overflow-auto pb-2">
                                    {mainTab.inner_tabs.map((innerTab) => (
                                      <button
                                        key={innerTab.id}
                                        className={`btn btn-sm me-2 flex-shrink-0 ${innerTab.id === activeInnerTab
                                            ? 'btn-primary'
                                            : 'btn-outline-primary'
                                          }`}
                                        onClick={() => setActiveInnerTab(innerTab.id)}
                                      >
                                        {innerTab.title}
                                      </button>
                                    ))}
                                  </div>
                                </div>
                              </div>
                            )}

                            <div className="mobile-tab-content">
                              {hasInnerTabs && activeInnerTabForMobile ? (
                                renderBlocksForTab(activeInnerTabForMobile.id)
                              ) : (
                                renderBlocksForTab(mainTab.id)
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          )}
        </div>
      </section>

    </>
  );
};

export default DynamicPages;